import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import FormattedDatePicker from "components/common/FormattedDatePicker";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import StatusReason from "components/common/StatusReason";
import StatusSelect from "components/common/StatusSelectWithChecks";
import Toggle from "components/common/Toggle";
import Visible from "components/common/Visible";
import ActiveLayerPremiumAdjustmentsEditor from "components/layers/ActiveLayerPremiumAdjustmentsEditor";
import ActiveLayerSelector from "components/layers/ActiveLayerSelector";
import BusinessUnitSelector from "components/layers/BusinessUnitSelector";
import FollowedPolicySelector from "components/layers/FollowedPolicySelector";
import IncludeSir from "components/layers/IncludeSir";
import LayerTransaction from "components/layers/LayerTransaction";
import PolicyNumberField from "components/layers/PolicyNumberField";
import PolicyType from "components/layers/PolicyType";
import ProcurementSelector from "components/layers/ProcurementSelector";
import ReinsuranceSelector from "components/layers/ReinsuranceSelector";
import RenewedFromLayerSelector from "components/layers/RenewedFromLayerSelector";
import RetroactiveDate from "components/layers/RetroactiveDate";
import TriaStatus from "components/layers/TriaStatus";
import PaymentTermsForm from "components/payments/PaymentTermsForm";
import PolicyForm from "components/pricing/layerPricing/PolicyForm";
import * as config from "config";
import * as labels from "config.labels";
import { Fragment } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingStore from "store/programPricing";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const RegionalCoverage = ({ layerId }) => {
  const dispatch = useDispatch();

  const regionalCoverage = useSelector(programPricingStore.select)?.layers?.[
    layerId
  ]?.regionalCoverage;

  const setRegionalCoverage = (regionalCoverage) => {
    dispatch(
      programPricingStore.updatePathValue({
        path: ["layers", layerId, "regionalCoverage"],
        value: regionalCoverage,
      })
    );
  };

  const { data: catExposuresConfig } = staticDataService.useConfigQuery(
    "cat_exposures"
  );

  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const regionalCoverageOptions =
    catExposuresConfig?.enums?.regionalCoverage?.choices ?? [];

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={
          regionalCoverageOptions.filter(
            (coverage) => coverage.key === regionalCoverage
          )?.[0] ?? (regionalCoverage == null ? null : { name: "<Unknown>" })
        }
        onChange={(_, value) => setRegionalCoverage(value?.key ?? null)}
        options={regionalCoverageOptions}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translateLabel(labels.REGIONAL_COVERAGE, {
              default: "Regional",
            })}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

const PerilsCoverage = ({ layerId }) => {
  const dispatch = useDispatch();

  const perilsCoverage = useSelector(programPricingStore.select)?.layers?.[
    layerId
  ]?.perilsCoverage;

  const setPerilsCoverage = (perilsCoverage) => {
    dispatch(
      programPricingStore.updatePathValue({
        path: ["layers", layerId, "perilsCoverage"],
        value: perilsCoverage,
      })
    );
  };

  const { data: catExposuresConfig } = staticDataService.useConfigQuery(
    "cat_exposures"
  );

  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const perilsCoverageOptions =
    catExposuresConfig?.enums?.perilsCoverage?.choices ?? [];

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={
          perilsCoverageOptions.filter(
            (coverage) => coverage.key === perilsCoverage
          )?.[0] ?? (perilsCoverage == null ? null : { name: "<Unknown>" })
        }
        onChange={(_, value) => setPerilsCoverage(value?.key ?? null)}
        options={perilsCoverageOptions}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translateLabel(labels.PERILS_COVERAGE, {
              default: "Perils",
            })}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

const LayerEditor = ({
  targetPerspective = dates.ProratedPerspective.PRORATED,
  proRataFactor = 1,
  ...props
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  if (props.layer == null) {
    return <></>;
  }

  const generatePolicyLocked =
    props.generatePolicyLockUntilBound && !props.layer.bound;

  const adjustValueForDisplay = (value) => {
    if (value === null || value === undefined) {
      return value;
    }

    if (
      proRataFactor === 1 ||
      targetPerspective === dates.ProratedPerspective.PRORATED
    ) {
      return value;
    }

    return proRataFactor ? value / proRataFactor : null;
  };

  const adjustValueForStorage = (value) => {
    if (value === null || value === undefined) {
      return value;
    }

    if (
      proRataFactor === 1 ||
      targetPerspective === dates.ProratedPerspective.PRORATED
    ) {
      return value;
    }

    return value * proRataFactor;
  };

  const getHelperText = (value) => {
    if (
      value === null ||
      value === undefined ||
      proRataFactor === 1 ||
      !proRataFactor
    ) {
      return undefined;
    }

    let altValue;
    if (targetPerspective === dates.ProratedPerspective.ANNUALIZED) {
      altValue = value;
    } else {
      altValue = value / proRataFactor;
    }

    const altPerspectiveLabel =
      targetPerspective === dates.ProratedPerspective.PRORATED
        ? "Annualized: "
        : "Pro Rated: ";

    return `${altPerspectiveLabel}${numbers.currencyFormat(altValue)}`;
  };

  return (
    <>
      <Grid item xs={12}>
        <ActiveLayerSelector />
      </Grid>

      {props.layer.customOverrides && (
        <Grid item xs={12}>
          <Alert severity={"warning"}>
            {"Custom overrides have been applied to this layer. These will be"}
            {"reflected in the bordereau report but not in Marmalade."}
            <br />
            <br />
            {Object.entries(props.layer.customOverrides).map(([k, v]) => (
              <Fragment key={k}>
                {k}
                {":"}
                <br />
                {Object.entries(v).map(([k, v]) => (
                  <Fragment key={k}>
                    &nbsp;&nbsp;&nbsp;&nbsp;{k}
                    {": "}
                    {JSON.stringify(v)}
                    <br />
                  </Fragment>
                ))}
              </Fragment>
            ))}
            <br />
            <br />
            <Button
              variant={"contained"}
              color={"error"}
              onClick={() => props.deleteActiveLayerCustomOverrides()}
            >
              {"Delete These Overrides"}
            </Button>
          </Alert>
        </Grid>
      )}

      {/* Layer Structure & Limits */}
      <Visible
        byTag={"<ActiveLayerEditor>.layerStructure"}
        defaultVisible={true}
      >
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"Layer Structure"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(labels.ACTIVE_LAYER_PANEL_LIMIT_LABEL, {
                    default: "Limit",
                  })}
                  variant={"filled"}
                  onChangeNumber={props.updateActiveLayerLimit}
                  value={props.layer.limit}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_ATTACHMENT_LABEL,
                    {
                      default: "Attachment",
                    }
                  )}
                  variant={"filled"}
                  onChangeNumber={props.updateActiveLayerAttachment}
                  value={props.layer.attachment}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Visible
                byTag={"<ActiveLayerEditor>.aggregateLimit"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_AGGREGATE_LIMIT_LABEL,
                      { default: "Aggregate Limit" }
                    )}
                    variant={"filled"}
                    onChangeNumber={(aggregateLimit) => {
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { aggregateLimit },
                      });
                    }}
                    value={props.layer.aggregateLimit}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.pcoAggregate"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_PCO_AGGREGATE_LABEL,
                      { default: "PCO Aggregate" }
                    )}
                    variant={"filled"}
                    onChangeNumber={(pcoAggregate) => {
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { pcoAggregate },
                      });
                    }}
                    value={props.layer.pcoAggregate}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.includeSir"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <IncludeSir layer={props.layer} />
                </Grid>
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_EFFECTIVE_ATTACHMENT_LABEL,
                      { default: "Effective Attachment" }
                    )}
                    variant={"filled"}
                    value={props.layer.effectiveAttachment ?? null}
                    disabled={true}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_AUTHORIZED_LIMIT_LABEL,
                    { default: "Authorized Limit" }
                  )}
                  variant={"filled"}
                  onChangeNumber={props.updateActiveLayerAuthorizedLimit}
                  value={props.layer.authorizedLimit}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Premium & Share */}
      <Visible
        byTag={"<ActiveLayerEditor>.premiumAndShare"}
        defaultVisible={true}
      >
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"Premium & Share"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(labels.REQUIRED_PREMIUM, {
                    default: "100% Premium",
                  })}
                  variant={"filled"}
                  onChangeNumber={(value) =>
                    props.updateActiveLayerGrossPremium(
                      adjustValueForStorage(value)
                    )
                  }
                  value={adjustValueForDisplay(props.layer.grossPremium)}
                  helperText={getHelperText(props.layer.grossPremium)}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_GROSS_PPM_LABEL,
                    {
                      default: "Gross PPM",
                    }
                  )}
                  variant={"filled"}
                  onChangeNumber={(value) =>
                    props.updateActiveLayerGrossPPM(
                      adjustValueForStorage(value)
                    )
                  }
                  value={adjustValueForDisplay(props.layer.grossPPM)}
                  helperText={getHelperText(props.layer.grossPPM)}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_OUR_LIMIT_LABEL,
                    {
                      default: "Our Limit",
                    }
                  )}
                  variant={"filled"}
                  onChangeNumber={props.updateActiveLayerLineSizeAbsolute}
                  value={props.layer.lineSize}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_OUR_SHARE_LABEL,
                    {
                      default: "Our Share",
                    }
                  )}
                  variant={"percentage"}
                  onChangeNumber={props.updateActiveLayerShareOfLine}
                  value={props.layer.shareOfLine}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Visible
                byTag={"<ActiveLayerEditor>.shareOfPremiumVisible"}
                defaultVisible={true}
              >
                <Grid item xs={6}>
                  <Disabled
                    byTag={"<ActiveLayerEditor>.shareOfPremium"}
                    defaultDisabledByTag={true}
                    force={props.layer.loadingPurePremium}
                  >
                    <PrettyNumberTextField
                      type={"text"}
                      InputLabelProps={{ shrink: true }}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_OUR_PREMIUM_LABEL,
                        { default: "Our Premium" }
                      )}
                      variant={"filled"}
                      onChangeNumber={(value) =>
                        props.updateActiveLayerShareOfPremium(
                          adjustValueForStorage(value)
                        )
                      }
                      value={adjustValueForDisplay(props.layer.shareOfPremium)}
                      helperText={getHelperText(props.layer.shareOfPremium)}
                      fullWidth
                    />
                  </Disabled>
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.grossEstLossRatio"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_GELR_LABEL,
                      {
                        default: "GELR",
                      }
                    )}
                    variant={"percentage"}
                    value={props.layer.grossEstLossRatio}
                    onChangeNumber={(grossEstLossRatio) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { grossEstLossRatio },
                      })
                    }
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.riskAdjustedReturnOnCapital"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={"RARC"}
                    variant={"percentage"}
                    value={props.layer.riskAdjustedReturnOnCapital}
                    onChangeNumber={(riskAdjustedReturnOnCapital) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { riskAdjustedReturnOnCapital },
                      })
                    }
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Costs & Margins */}
      <Visible
        byTag={"<ActiveLayerEditor>.costsAndMargins"}
        defaultVisible={true}
      >
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"Costs & Margins"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ spellCheck: "false" }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_BROKERAGE_LABEL,
                    {
                      default: "Brokerage",
                    }
                  )}
                  autoComplete={"off"}
                  variant={"percentage"}
                  onChangeNumber={props.updateActiveLayerBrokerage}
                  value={props.layer.brokerage}
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Visible byTag={"<ActiveLayerEditor>.oac"} defaultVisible={true}>
                <Tooltip title={"Other Acquisition Costs %"}>
                  <Grid item xs={6}>
                    <PrettyNumberTextField
                      type={"text"}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ spellCheck: "false" }}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_OAC_LABEL,
                        {
                          default: "OAC",
                        }
                      )}
                      autoComplete={"off"}
                      variant={"percentage"}
                      onChangeNumber={
                        props.updateActiveLayerOtherAcquisitionCosts
                      }
                      value={props.layer.otherAcquisitionCosts}
                      disabled={props.layer.loadingPurePremium}
                      fullWidth
                    />
                  </Grid>
                </Tooltip>
              </Visible>
              <Visible
                defaultVisible={true}
                byTag={"<ActiveLayerEditor>.target-profit"}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={translateLabel(labels.TARGET_PROFIT, {
                      default: "Target Profit",
                    })}
                    autoComplete={"off"}
                    variant={"percentage"}
                    onChangeNumber={props.updateActiveLayerProfit}
                    value={props.layer.profit}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.expenses"}
                defaultVisible={true}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_EXPENSES_LABEL,
                      { default: "Expenses" }
                    )}
                    autoComplete={"off"}
                    variant={"percentage"}
                    onChangeNumber={props.updateActiveLayerExpenses}
                    value={props.layer.expenses}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                defaultVisible={false}
                byTag={"<ActiveLayerEditor>.slip-fee"}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_SLIP_FEE_LABEL,
                      { default: "Slip Fee" }
                    )}
                    autoComplete={"off"}
                    variant={"percentage"}
                    onChangeNumber={(slipFee) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { slipFee },
                      })
                    }
                    value={props.layer.slipFee}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                defaultVisible={false}
                byTag={"<ActiveLayerEditor>.gnulr"}
              >
                <Grid item xs={6}>
                  <Disabled
                    byTag={"<ActiveLayerEditor>.gnulr"}
                    defaultDisabledByTag={false}
                  >
                    <PrettyNumberTextField
                      type={"text"}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ spellCheck: "false" }}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_GNULR_LABEL,
                        {
                          default: "GNULR",
                        }
                      )}
                      autoComplete={"off"}
                      variant={"percentage"}
                      onChangeNumber={(e) => {
                        const inputValue = 100 - e ?? 100;
                        props.updateActiveLayerProfit(inputValue);
                      }}
                      value={100 - props.layer.profit}
                      disabled={props.layer.loadingPurePremium}
                      fullWidth
                    />
                  </Disabled>
                </Grid>
              </Visible>
              <Visible
                defaultVisible={false}
                byTag={"<ActiveLayerEditor>.minimumEarnedPremium"}
              >
                <Grid item xs={6}>
                  <Disabled
                    byTag={"<ActiveLayerEditor>.minimumEarnedPremium"}
                    defaultDisabledByTag={false}
                  >
                    <PrettyNumberTextField
                      type={"text"}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ spellCheck: "false" }}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_MINIMUM_EARNED_PREMIUM_LABEL,
                        { default: "Minimum Earned Premium" }
                      )}
                      autoComplete={"off"}
                      variant={"percentage"}
                      onChangeNumber={(e) => {
                        props.updateActiveLayerMinimumEarnedPremium(e);
                      }}
                      value={props.layer.minimumEarnedPremium}
                      disabled={props.layer.loadingPurePremium}
                      fullWidth
                    />
                  </Disabled>
                </Grid>
              </Visible>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Coverage & Policy Details */}
      <Visible
        byTag={"<ActiveLayerEditor>.coverageAndPolicyDetails"}
        defaultVisible={true}
      >
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"Coverage & Policy Details"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Visible
                byTag={"<ActiveLayerEditor>.regionalCoverage"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <RegionalCoverage layerId={props?.layer?.id} />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.perilsCoverage"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PerilsCoverage layerId={props?.layer?.id} />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.inceptionDate"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <FormattedDatePicker
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_INCEPTION_LABEL,
                      {
                        default: "Inception",
                      }
                    )}
                    onChangeDate={props.updateInception}
                    value={props.program.inception}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                    clearable={false}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.dateReceived"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <FormattedDatePicker
                    label={translateLabel(labels.DATE_RECEIVED, {
                      default: "Date Received",
                    })}
                    onChangeDate={(dateReceived) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { dateReceived },
                      })
                    }
                    value={props.layer.dateReceived ?? ""}
                    fullWidth
                    clearable={false}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.quoteDate"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <FormattedDatePicker
                    label={translateLabel(labels.QUOTE_DATE, {
                      default: "Quote Date",
                    })}
                    onChangeDate={(quoteDate) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { quoteDate },
                      })
                    }
                    value={props.layer.quoteDate ?? ""}
                    fullWidth
                    clearable={false}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.policyType"}
                defaultVisible={false}
              >
                <Grid item xs={12}>
                  <Disabled ifReadOnly>
                    <PolicyType />
                  </Disabled>
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.transaction"}
                defaultVisible={false}
              >
                <Disabled ifReadOnly>
                  <LayerTransaction disabled={props.layer.loadingPurePremium} />
                </Disabled>
              </Visible>
              <Grid item xs={12}>
                <PolicyForm disabled={props.layer.loadingPurePremium} />
              </Grid>
              <Disabled ifReadOnly>
                <RetroactiveDate disabled={props.layer.loadingPurePremium} />
              </Disabled>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Administration & References */}
      <Visible
        byTag={"<ActiveLayerEditor>.administrationAndReferences"}
        defaultVisible={true}
      >
        <Grid item xs={12}>
          <Component title={"Administration & References"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Visible
                byTag={"<ActiveLayerEditor>.followedPolicy"}
                defaultVisible={false}
              >
                <Grid item xs={12}>
                  <FollowedPolicySelector
                    disabled={props.layer.loadingPurePremium}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.reinsurance"}
                defaultVisible={false}
              >
                <Grid item xs={12} md={6}>
                  <ReinsuranceSelector
                    label={translateLabel(labels.LAYER_REINSURANCE, {
                      default: "Security",
                    })}
                    value={props.layer.paperProvider}
                    onChange={(x) => {
                      props.updateActiveLayerReinsurance(x);
                    }}
                    disabled={props.layer.loadingPurePremium}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.procurement"}
                defaultVisible={false}
              >
                <Grid item xs={12} md={6}>
                  <ProcurementSelector
                    disabled={props.layer.loadingPurePremium}
                  />
                </Grid>
              </Visible>
              <Visible byTag={"<ActiveLayerEditor>.paymentTerms"}>
                <Grid item xs={12}>
                  <PaymentTermsForm
                    paymentTerms={props.layer.paymentTerms}
                    setPaymentTerms={props.updateActiveLayerPaymentTerms}
                  />
                </Grid>
              </Visible>
              <Visible byTag={"<ActiveLayerEditor>.businessUnit"}>
                <Grid item xs={12} md={6}>
                  <BusinessUnitSelector
                    value={props.layer.businessUnit}
                    onChange={(businessUnit) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { businessUnit },
                      })
                    }
                  />
                </Grid>
              </Visible>
              <Grid item xs={12} md={6}>
                <Toggle
                  byTag={"<ActiveLayerEditor>.reference.generatePolicyNumber"}
                  trueChild={
                    <PolicyNumberField
                      value={props.layer.reference}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_LAYER_REFERENCE_LABEL,
                        { default: "Reference" }
                      )}
                      onChange={props.updateActiveLayerReference}
                      disabled={props.layer.loadingPurePremium}
                      generateLocked={generatePolicyLocked}
                    />
                  }
                  falseChild={
                    <TextField
                      type={"text"}
                      InputLabelProps={{ shrink: true }}
                      label={translateLabel(
                        labels.ACTIVE_LAYER_PANEL_LAYER_REFERENCE_LABEL,
                        { default: "Reference" }
                      )}
                      variant={"filled"}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (!value || value === "") {
                          value = null;
                        }
                        props.updateActiveLayerReference(value);
                      }}
                      value={props.layer.reference ?? ""}
                      disabled={props.layer.loadingPurePremium}
                      fullWidth
                    />
                  }
                />
              </Grid>
              <Visible
                byTag={"<ActiveLayerEditor>.puniReference"}
                defaultVisible={false}
              >
                <Grid item xs={12} md={6}>
                  <PolicyNumberField
                    type={"puniReference"}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_PUNI_REFERENCE_LABEL,
                      { default: "Puni Reference" }
                    )}
                    value={props.layer.puniReference}
                    onChange={(puniReference) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { puniReference },
                      })
                    }
                    disabled={props.layer.loadingPurePremium}
                    generateLocked={generatePolicyLocked}
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.externalSystemReference"}
                defaultVisible={false}
              >
                <Grid item xs={12} md={6}>
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_EXTERNAL_SYSTEM_REFERENCE_LABEL,
                      { default: "External System Reference" }
                    )}
                    variant={"filled"}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (!value || value === "") {
                        value = null;
                      }
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { externalSystemReference: value },
                      });
                    }}
                    value={props.layer.externalSystemReference ?? ""}
                    disabled={props.layer.loadingPurePremium}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.renewedFromLayer"}
                defaultVisible
              >
                {props.program?.priorSubmission && (
                  <Grid item xs={12} md={6}>
                    <RenewedFromLayerSelector
                      disabled={props.layer.loadingPurePremium}
                    />
                  </Grid>
                )}
              </Visible>
              <Grid item xs={12}>
                <TextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ spellCheck: "false" }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_COMMENT_LABEL,
                    {
                      default: "Comment",
                    }
                  )}
                  autoComplete={"off"}
                  variant={"filled"}
                  onChange={(e) => props.updateComment(e.target.value)}
                  value={props.layer.comment || ""}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StatusSelect
                  layer={props.layer}
                  status={props.layer.status}
                  disabled={props.layer.loadingPurePremium}
                  onChange={(status) => {
                    props.updateActiveLayerStatus(status);
                  }}
                />
              </Grid>
              <Visible byTag={"<ActiveLayerEditor>.statusReason"}>
                <Grid item xs={12} md={6}>
                  <StatusReason
                    status={props.layer.status}
                    reason={props.layer.statusReason}
                    onChange={(statusReason) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: { statusReason },
                      })
                    }
                  />
                </Grid>
              </Visible>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Advanced Features - only show if enabled */}
      <Visible byTag={"<ActiveLayerEditor>.TRIA"} defaultVisible={false}>
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"TRIA"}>
            <Grid container spacing={config.GRID_SPACING}>
              <TriaStatus
                layer={props.layer}
                updateLayer={(values) =>
                  props.updateLayer({
                    layerId: props.layer.id,
                    values,
                  })
                }
              />
              <Visible
                byTag={"<ActiveLayerEditor>.TRIAAmount"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_TRIA_AMOUNT_LABEL,
                      { default: "TRIA Amount" }
                    )}
                    variant={"filled"}
                    disabled={true}
                    value={adjustValueForDisplay(props.layer.TRIAAmount ?? 0)}
                    helperText={getHelperText(props.layer.TRIAAmount ?? 0)}
                    fullWidth
                  />
                </Grid>
              </Visible>
              <Visible
                byTag={"<ActiveLayerEditor>.shareOfPremiumWithTRIA"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_OUR_PREMIUM_INC_TRIA_LABEL,
                      { default: "Our Premium (Inc. TRIA)" }
                    )}
                    variant={"filled"}
                    disabled={true}
                    value={adjustValueForDisplay(
                      props.layer.shareOfPremiumWithTRIA ?? 0
                    )}
                    helperText={getHelperText(
                      props.layer.shareOfPremiumWithTRIA ?? 0
                    )}
                    fullWidth
                  />
                </Grid>
              </Visible>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      <Visible byTag={"<ActiveLayerEditor>.puniWrap"} defaultVisible={false}>
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"Puni Wrap"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_WRAP_LABEL,
                    { default: "Puni Wrap (%)" }
                  )}
                  variant={"percentage"}
                  value={props.layer.puniWrapPct ?? 0}
                  onChangeNumber={(puniWrapPct) =>
                    props.updateLayer({
                      layerId: props.layer.id,
                      values: { puniWrapPct },
                    })
                  }
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_WRAP_PREMIUM_LABEL,
                    { default: "Our Puni Wrap Premium" }
                  )}
                  variant={"filled"}
                  disabled={true}
                  value={adjustValueForDisplay(
                    props.layer.shareOfPuniWrap ?? 0
                  )}
                  helperText={getHelperText(props.layer.shareOfPuniWrap ?? 0)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_INCLUDED_LABEL,
                    { default: "Puni Included?" }
                  )}
                  control={
                    <Checkbox
                      checked={props.layer.isPuniWrapIncluded ?? false}
                      onChange={(e) =>
                        props.updateLayer({
                          layerId: props.layer.id,
                          values: {
                            isPuniWrapIncluded: e.target.checked,
                          },
                        })
                      }
                      disabled={props.layer.loadingPurePremium}
                    />
                  }
                  componentsProps={{
                    typography: {
                      variant: "caption",
                    },
                  }}
                  sx={{
                    height: "100%",
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_PREMIUM_EX_WRAP_LABEL,
                    { default: "Our Premium ex Puni Wrap" }
                  )}
                  variant={"filled"}
                  disabled={true}
                  value={adjustValueForDisplay(
                    props.layer.shareOfPremiumExPuniWrap ?? 0
                  )}
                  helperText={getHelperText(
                    props.layer.shareOfPremiumExPuniWrap ?? 0
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_BROKERAGE_LABEL,
                    { default: "Puni Brokerage (%)" }
                  )}
                  variant={"percentage"}
                  value={props.layer.puniWrapBrokeragePct}
                  onChangeNumber={(puniWrapBrokeragePct) =>
                    props.updateLayer({
                      layerId: props.layer.id,
                      values: { puniWrapBrokeragePct },
                    })
                  }
                  disabled={props.layer.loadingPurePremium}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(
                    labels.ACTIVE_LAYER_PANEL_PUNI_WRAP_BROKERAGE_LABEL,
                    { default: "Our Puni Wrap Brokerage" }
                  )}
                  variant={"filled"}
                  disabled={true}
                  value={adjustValueForDisplay(
                    props.layer.shareOfPuniWrapBrokerage ?? 0
                  )}
                  helperText={getHelperText(
                    props.layer.shareOfPuniWrapBrokerage ?? 0
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      <Visible byTag={"<ActiveLayerEditor>.facPct"} defaultVisible={false}>
        <Grid item md={6} sm={12} xs={12}>
          <Component title={"FAC"}>
            <Grid container spacing={config.GRID_SPACING}>
              <Grid item xs={6}>
                <PrettyNumberTextField
                  type={"text"}
                  InputLabelProps={{ shrink: true }}
                  label={translateLabel(labels.ACTIVE_LAYER_PANEL_FAC_LABEL, {
                    default: "Fac (%)",
                  })}
                  variant={"percentage"}
                  value={props.layer.facPct ?? 0}
                  onChangeNumber={(facPct) =>
                    props.updateLayer({
                      layerId: props.layer.id,
                      values: { facPct },
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Visible
                byTag={"<ActiveLayerEditor>.facPremium"}
                defaultVisible={false}
              >
                <Grid item xs={6}>
                  <PrettyNumberTextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    label={translateLabel(
                      labels.ACTIVE_LAYER_PANEL_FAC_PREMIUM_LABEL,
                      { default: "Fac Premium" }
                    )}
                    variant={"filled"}
                    value={adjustValueForDisplay(props.layer.facPremium ?? 0)}
                    helperText={getHelperText(props.layer.facPremium ?? 0)}
                    onChangeNumber={(facPremium) =>
                      props.updateLayer({
                        layerId: props.layer.id,
                        values: {
                          facPremium: adjustValueForStorage(facPremium),
                        },
                      })
                    }
                    fullWidth
                  />
                </Grid>
              </Visible>
            </Grid>
          </Component>
        </Grid>
      </Visible>

      {/* Premium Adjustments */}
      <Visible
        byTag={"pricing.layerPricing.premiumAdjustments"}
        defaultVisible={true}
      >
        <Grid item xs={12}>
          <ActiveLayerPremiumAdjustmentsEditor />
        </Grid>
      </Visible>
    </>
  );
};

export const mapDispatchToProps = {
  updateComment: pricingActions.updateComment,
  refreshActiveLayerPricing: pricingActions.refreshActiveLayerPricing,
  updateLayer: pricingActions.updateLayer,
  updateActiveLayerLimit: pricingActions.updateActiveLayerLimit,
  updateActiveLayerAttachment: pricingActions.updateActiveLayerAttachment,
  updateActiveLayerLineSizeAbsolute:
    pricingActions.updateActiveLayerLineSizeAbsolute,
  updateActiveLayerGrossPremium: pricingActions.updateActiveLayerGrossPremium,
  updateActiveLayerAuthorizedLimit:
    pricingActions.updateActiveLayerAuthorizedLimit,
  updateInception: programActions.updateInception,
  updateActiveLayerStatus: pricingActions.updateActiveLayerStatus,
  updateBaseLayerLimit: pricingActions.updateBaseLayerLimit,
  updateBaseLayerAttachment: pricingActions.updateBaseLayerAttachment,
  updateActiveLayerShareOfLine: pricingActions.updateActiveLayerShareOfLine,
  updateActiveLayerTRIA: pricingActions.updateActiveLayerTRIA,
  updateActiveLayerShareOfPremium:
    pricingActions.updateActiveLayerShareOfPremium,
  updateActiveLayerGrossPPM: pricingActions.updateActiveLayerGrossPPM,
  updateActiveLayerExpenses: pricingActions.updateActiveLayerExpenses,
  updateActiveLayerProfit: pricingActions.updateActiveLayerProfit,
  updateActiveLayerBrokerage: pricingActions.updateActiveLayerBrokerage,
  updateActiveLayerOtherAcquisitionCosts:
    pricingActions.updateActiveLayerOtherAcquisitionCosts,
  updateActiveLayerReference: pricingActions.updateActiveLayerReference,
  updateActiveLayerPaymentTerms: pricingActions.updateActiveLayerPaymentTerms,
  updateActiveLayerReinsurance: pricingActions.updateActiveLayerReinsurance,
  updateActiveLayerMinimumEarnedPremium:
    pricingActions.updateActiveLayerMinimumEarnedPremium,
  updateNote: pricingActions.updateLayerPricingNote,
  deleteActiveLayerCustomOverrides:
    pricingActions.deleteActiveLayerCustomOverrides,
};

export const mapStateToProps = (state) => {
  return {
    layer: pricingSelectors.selectResolvedActiveLayer(state),
    program: programSelectors.selectResolvedProgram(state),
    generatePolicyLockUntilBound:
      userSelectors.selectUserConfig(state)?.componentVisibility?.[
        "generatePolicyNumber.lockUntilBound"
      ] ?? false,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LayerEditor);
